import * as R from 'ramda';
import React from 'react';
// components
import { Label } from '../label';
// components
import { fieldNameToTransform, getTripStatusOptions, getOrderStatusOptions } from '../filter/settings';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
import { getGroupedByCountryStateOptionsByConfig } from '../../helpers/options';
// icons
import * as I from '../../svgs';
//////////////////////////////////////////////////

export const REPORT_FIELD_DND_ID = 'reportFieldDndId';

export const createReportFieldDndId = () => `report-field-${G.generateGuid()}`;

export const ensureReportFieldDndId = (field = {}) => {
  if (G.isNotNilAndNotEmpty(R.prop(REPORT_FIELD_DND_ID, field))) return field;

  return R.assoc(REPORT_FIELD_DND_ID, createReportFieldDndId(), field);
};

export const ensureReportFieldListDndIds = (fields = []) => (
  R.map(ensureReportFieldDndId, fields)
);

export const omitReportFieldDndId = (field = {}) => (
  R.omit([REPORT_FIELD_DND_ID], field)
);

export const omitReportFieldDndIds = (fields = []) => (
  R.map(omitReportFieldDndId, fields)
);

export const getReportFieldSortableId = (field = {}, index) => {
  const reportFieldDndId = R.prop(REPORT_FIELD_DND_ID, field);

  if (G.isNotNilAndNotEmpty(reportFieldDndId)) return reportFieldDndId;

  const guid = R.prop(GC.FIELD_GUID, field);

  if (G.isNotNilAndNotEmpty(guid)) return `report-field-${guid}`;

  return `report-field-list-dnd-item-${R.or(R.prop('sequence', field), index)}`;
};

export const SORT_FIELD_DND_ID = 'sortFieldDndId';

export const createSortFieldDndId = () => `report-sort-${G.generateGuid()}`;

export const ensureSortFieldDndId = (field = {}) => {
  if (G.isNotNilAndNotEmpty(R.prop(SORT_FIELD_DND_ID, field))) return field;

  return R.assoc(SORT_FIELD_DND_ID, createSortFieldDndId(), field);
};

export const ensureSortFieldListDndIds = (fields = []) => (
  R.map(ensureSortFieldDndId, fields)
);

export const omitSortFieldDndId = (field = {}) => (
  R.omit([SORT_FIELD_DND_ID], field)
);

export const omitSortFieldDndIds = (fields = []) => (
  R.map(omitSortFieldDndId, fields)
);

export const getSortFieldSortableId = (field = {}, index) => {
  const sortFieldDndId = R.prop(SORT_FIELD_DND_ID, field);

  if (G.isNotNilAndNotEmpty(sortFieldDndId)) return sortFieldDndId;

  const guid = R.prop(GC.FIELD_GUID, field);

  if (G.isNotNilAndNotEmpty(guid)) return `report-sort-${guid}`;

  return `report-sort-list-dnd-item-${R.or(R.prop('sequence', field), index)}`;
};

export const getDnDReorderResult = (event, sortableIds) => {
  const overId = R.path(['over', 'id'], event);
  const activeId = R.path(['active', 'id'], event);

  if (R.or(R.isNil(overId), R.equals(activeId, overId))) return null;

  const sourceIndex = R.indexOf(activeId, sortableIds);
  const destinationIndex = R.indexOf(overId, sortableIds);

  if (R.or(R.equals(sourceIndex, -1), R.equals(destinationIndex, -1))) return null;

  return {
    source: { index: sourceIndex },
    destination: { index: destinationIndex },
  };
};

export const getValue = (input, options) => (
  R.find((option) => (
      R.or(
        R.equals(option.name, input.value),
        R.equals(option.value, input.value),
      )),
    options,
  )
);

export const buttons = (handleUseAction) => ([
  {
    width: 'auto',
    type: 'button',
    action: handleUseAction,
    btnText: (
      <Label fontWeight={700} frontIcon={I.play(G.getTheme('icons.iconColorWhite'))} >
        {G.getWindowLocale('actions:use', 'Use')}
      </Label>
    ),
  },
]);

export const mapFieldsWithSequenceFromIndex = (fields) => G.mapIndexed(
  (field, index) => R.assoc('sequence', R.inc(index), field),
  fields,
);

export const buildPivotComplexReport = (props, columnSettings, isCreate) => {
  const { theme, selectedReport } = props;

  const complexReport = flexMonsterPivotInstance.getReport(); // eslint-disable-line

  const pivotSettings = R.compose(
    R.assoc('theme', theme),
    R.omit(['dataSource']),
  )(complexReport);

  const report = G.isTrue(isCreate)
    ? G.omitObjectChildArrayItemSystemFields('fields', G.omitObjectSystemFields(selectedReport))
    : selectedReport;

  return R.omit(['orderFields'], {
    ...report,
    pivotSettings: JSON.stringify(pivotSettings),
  });
};

const createSelectMultipleValue = (item) => {
  if (G.isNilOrEmpty(R.path(['selectMultipleValue'], item))) return '';
  return R.join(',', R.map(R.prop('value'), R.path(['selectMultipleValue'], item)));
};

const checkDataTypeInSearchCriteriaObject = (item) => R.compose(
  R.set(R.lensProp('dataType'), 'string'),
  R.assoc('stringValue', createSelectMultipleValue(item)),
  R.omit(['selectMultipleValue']),
)(item);

const getDateOnlySearchCriteriaValue = (value) => (
  G.isNilOrEmpty(value)
    ? value
    : `${G.convertInstanceToDefaultDateFormat(value)} ${GC.DEFAULT_FILTER_TIME}`
);

const getTimeSearchCriteriaValue = (value) => {
  if (G.isNilOrEmpty(value)) return value;

  const timeValue = R.compose(R.join(' '), R.takeLast(2), R.split(' '))(value);

  return `${GC.DEFAULT_FILTER_DATE} ${G.convertTimeToFormat(timeValue, GC.DEFAULT_TIME_FORMAT)}`;
};

const normalizeDateOnlySearchCriteria = (item) => {
  const { to, from, operation } = item;

  const toValue = G.ifElse(R.equals(operation, 'equal'), from, to);

  return {
    ...item,
    to: getDateOnlySearchCriteriaValue(toValue),
    from: getDateOnlySearchCriteriaValue(from),
    dataType: 'date',
  };
};

const normalizeTimeSearchCriteria = (item) => ({
  ...item,
  to: getTimeSearchCriteriaValue(R.prop('to', item)),
  from: getTimeSearchCriteriaValue(R.prop('from', item)),
  dataType: 'date',
});

const transformSearchItemByUomSystem = (item, reportType) => {
  const shouldNotTransform = R.includes(
    reportType,
    [
      GC.ITEM_REPORT,
      GC.CLO_ITEM_REPORT,
      GC.FLEET_TRUCK_REPORT,
      GC.FLEET_TRAILER_REPORT,
      GC.CONTAINER_TEMPLATE_REPORT,
      GC.COMPENSATION_TEMPLATE_REPORT,
      GC.FLEET_TRUCK_DAILY_LOG_REPORT,
      GC.FLEET_DRIVER_DAILY_LOG_REPORT,
      GC.FLEET_DRIVER_COMPENSATION_REPORT,
      GC.FLEET_VENDOR_COMPENSATION_REPORT,
    ],
  );

  if (shouldNotTransform) return item;

  const { operation, numberValue, numberRange, propertyName } = item;

  const isImperial = G.isImperialUomSystem();

  if (R.includes('distance', R.toLower(propertyName))) {
    if (R.and(isImperial, R.equals(operation, 'range'))) {
      const { to, from } = numberRange;

      const newNumberRange = {
        to: G.fromMilesToKmsNumber(to, 5),
        from: G.fromMilesToKmsNumber(from, 5),
      };

      return R.assoc('numberRange', newNumberRange, item);
    } else if (R.equals(operation, 'equal')) {
      const toValue = G.ifElse(
        isImperial,
        G.fromMilesToKmsNumber(R.add(numberValue, 1), 5),
        R.add(numberValue, 1),
      );

      const fromValue = G.ifElse(
        isImperial,
        G.fromMilesToKmsNumber(R.subtract(numberValue, 1), 5),
        R.subtract(numberValue, 1),
      );

      return {
        ...item,
        numberValue: null,
        operation: 'range',
        numberRange: {
          to: toValue,
          from: fromValue,
        },
      };
    }

    return R.assoc('numberValue', G.fromMilesToKmsNumber(numberValue, 5), item);
  }

  if (R.includes('temperature', R.toLower(propertyName))) {
    if (R.and(isImperial, R.equals(operation, 'range'))) {
      const { to, from } = numberRange;

      const newNumberRange = {
        to: G.fromFahrenheitToCelsius(to, 5),
        from: G.fromFahrenheitToCelsius(from, 5),
      };

      return R.assoc('numberRange', newNumberRange, item);
    } else if (R.equals(operation, 'equal')) {
      const toValue = G.ifElse(
        isImperial,
        G.fromFahrenheitToCelsius(R.add(numberValue, 1), 5),
        R.add(numberValue, 1),
      );

      const fromValue = G.ifElse(
        isImperial,
        G.fromFahrenheitToCelsius(R.subtract(numberValue, 1), 5),
        R.subtract(numberValue, 1),
      );

      return {
        ...item,
        numberValue: null,
        operation: 'range',
        numberRange: {
          to: toValue,
          from: fromValue,
        },
      };
    }

    return R.assoc('numberValue', G.fromFahrenheitToCelsius(numberValue, 5), item);
  }

  if (R.includes('weight', R.toLower(propertyName))) {
    if (R.and(isImperial, R.equals(operation, 'range'))) {
      const { to, from } = numberRange;

      const newNumberRange = {
        to: G.fromPoundsToKgs(to, 5),
        from: G.fromPoundsToKgs(from, 5),
      };

      return R.assoc('numberRange', newNumberRange, item);
    } else if (R.equals(operation, 'equal')) {
      const toValue = G.ifElse(
        isImperial,
        G.fromPoundsToKgs(R.add(numberValue, 1), 5),
        R.add(numberValue, 1),
      );

      const fromValue = G.ifElse(
        isImperial,
        G.fromPoundsToKgs(R.subtract(numberValue, 1), 5),
        R.subtract(numberValue, 1),
      );

      return {
        ...item,
        numberValue: null,
        operation: 'range',
        numberRange: {
          to: toValue,
          from: fromValue,
        },
      };
    }

    return R.assoc('numberValue', G.fromPoundsToKgs(numberValue, 5), item);
  }

  if (R.includes('volume', R.toLower(propertyName))) {
    if (R.and(isImperial, R.equals(operation, 'range'))) {
      const { to, from } = numberRange;

      const newNumberRange = {
        to: G.fromCubicFeetToMeter(to, 5),
        from: G.fromCubicFeetToMeter(from, 5),
      };

      return R.assoc('numberRange', newNumberRange, item);
    } else if (R.equals(operation, 'equal')) {
      const toValue = G.ifElse(
        isImperial,
        G.fromCubicFeetToMeter(R.add(numberValue, 1), 5),
        R.add(numberValue, 1),
      );

      const fromValue = G.ifElse(
        isImperial,
        G.fromCubicFeetToMeter(R.subtract(numberValue, 1), 5),
        R.subtract(numberValue, 1),
      );

      return {
        ...item,
        numberValue: null,
        operation: 'range',
        numberRange: {
          to: toValue,
          from: fromValue,
        },
      };
    }

    return R.assoc('numberValue', G.fromCubicFeetToMeter(numberValue, 5), item);
  }

  return item;
};

export const transformSearchCriteriaBeforeReportPost = (data, reportType) => R.compose(
  R.map((item) => transformSearchItemByUomSystem(item, reportType)),
  R.map((item) => {
    const { dataType, propertyName, referenceName, stringValue, referenceFieldName } = item;

    if (R.and(R.equals(propertyName, GC.FIELD_REFERENCES), G.isNotNilAndNotEmpty(referenceFieldName))) {
      const referencePrefix = `Ref (${G.getWindowLocale(...GC.referenceFieldNameLocaleMap[referenceFieldName])}): `;
      const value = R.slice(referencePrefix.length, Infinity, referenceName);

      return R.assoc(GC.FIELD_REFERENCE_NAME, value, item);
    }

    if (R.includes(dataType, ['selectMultiple', 'selectMultipleCreatable'])) {
      return checkDataTypeInSearchCriteriaObject(item);
    }

    if (R.equals(dataType, 'time')) return normalizeTimeSearchCriteria(item);

    if (R.equals(dataType, 'dateOnly')) return normalizeDateOnlySearchCriteria(item);

    const stringDataTypes = ['string:array', 'string:select'];

    if (R.includes(dataType, stringDataTypes)) return R.assoc('dataType', 'string', item);

    if (R.and(
      R.includes(reportType, [GC.ROUTE_TEL_REPORT, GC.ROUTE_CLO_REPORT]),
      R.equals(propertyName, GC.FIELD_STATUS),
    )) {
      const statusOptions = R.includes('TEL', reportType) ? getTripStatusOptions() : getOrderStatusOptions();
      const values = R.map(R.prop(GC.FIELD_VALUE), statusOptions);

      if (G.notContain(stringValue, values)) {
        return R.assoc(GC.FIELD_PROPERTY_NAME, GC.GRC.CUSTOM_STATUS_NAME, item);
      }
    }

    return item;
  }),
)(data);

export const transformPropDataFromSelectToString = (filterProps) => filterProps.map(
  (item) => {
    if (R.includes(item.type, ['selectMultiple', 'string:array', 'selectMultipleCreatable'])) {
      return R.assoc('type', 'string', item);
    }

    return item;
  },
);

export const buildReport = (props) => {
  const {
    prompt,
    isCreate,
    isUpdate,
    usedReport,
    formValues,
    showIssues,
    reportSortList,
    reportFieldList,
    showReportSummary,
    reportFilterGroups,
    disableSetReportFields,
    avoidRelatedValueDuplicates,
  } = props;

  let searchCriteria = R.compose(
    R.map((filter) => {
      const { guid, name, groupId, origValue, withoutOrGroup } = filter;
      const groupIdToUse = G.ifElse(G.isTrue(withoutOrGroup), null, groupId);
      const guidToUse = G.ifElse(isCreate, null, guid);

      return {
        ...filter,
        guid: guidToUse,
        groupId: groupIdToUse,
        [GC.FIELD_NAME]: R.or(origValue, name),
      };
    }),
    R.reject((field) => G.isNilOrEmpty(field.propertyName)),
    R.filter((field) => G.notEquals(field.dataType, '')),
    R.reduce(R.concat, []),
    R.values,
  )(reportFilterGroups);

  if (R.or(isCreate, isUpdate)) {
    searchCriteria = transformSearchCriteriaBeforeReportPost(
      searchCriteria,
      G.getPropFromObject(GC.FIELD_TYPE, usedReport),
    );
  }

  const orderFields = R.compose(
    G.mapIndexed((filter, i) => {
      const { guid, name, origValue } = filter;
      const fieldToUse = omitSortFieldDndId(filter);
      const guidToUse = G.ifElse(isCreate, null, guid);

      return R.mergeRight(fieldToUse, {
        sequence: R.inc(i),
        [GC.FIELD_GUID]: guidToUse,
        [GC.FIELD_NAME]: R.or(origValue, name),
      });
    }),
    R.filter(({ name }) => G.isNotNilAndNotEmpty(name)),
  )(reportSortList);
  let fieldsToUse = [];
  let summaryParams = {};

  if (R.not(disableSetReportFields)) {
    fieldsToUse = R.compose(
      R.map((field) => {
        const { guid, name, origValue } = field;
        const fieldToUse = omitReportFieldDndId(field);

        return R.mergeRight(fieldToUse, {
          [GC.FIELD_NAME]: R.or(origValue, name),
          [GC.FIELD_GUID]: G.ifElse(isCreate, null, guid),
        });
      }),
      R.filter((field) => G.notEquals(field.name, '')),
    )(reportFieldList);
  }

  if (showReportSummary) {
    summaryParams = {
      [GC.FIELD_GROUP_BY]: R.pathOr(null, [GC.FIELD_GROUP_BY], formValues),
      [GC.FIELD_SUMMARY_GROUP]: R.pathOr(null, [GC.FIELD_SUMMARY_GROUP], formValues),
      [GC.FIELD_SHOW_LAST_DAYS]: R.pathOr(null, [GC.FIELD_SHOW_LAST_DAYS], formValues),
      [GC.FIELD_SUMMARY_SUBGROUP]: R.pathOr(null, [GC.FIELD_SUMMARY_SUBGROUP], formValues),
      [GC.FIELD_GROUP_BY_SORT]: R.pathOr(GC.SORT_TYPE_DESC, [GC.FIELD_GROUP_BY_SORT], formValues),
    };
  }

  fieldsToUse = R.compose(
    R.filter(({ name }) => G.isNotNilAndNotEmpty(name)),
    mapFieldsWithSequenceFromIndex,
  )(fieldsToUse);

  return {
    ...usedReport,
    ...summaryParams,
    showIssues,
    orderFields,
    searchCriteria,
    fields: fieldsToUse,
    avoidRelatedValueDuplicates,
    prompt: G.ifElse(G.isNilOrEmpty(searchCriteria), false, prompt),
  };
};

export const checkReportFunction = (reports) => {
  const reportType = R.path([0, GC.FIELD_TYPE], reports);

  const checkIsStringValueNotNil = (stringValue) => {
    if (G.isNotNil(stringValue)) return R.split(',', stringValue);

    return '';
  };

  const searchCriteriaObject = (data) => {
    const filterValue = (option) => R.includes(option.value, checkIsStringValueNotNil(data.stringValue));

    const searchCriteriaOptions = R.map(
      (item) => item,
      R.filter(filterValue, fieldNameToTransform[data.propertyName](reportType)),
    );

    const newObject = R.compose(
      R.set(R.lensProp('dataType'), 'selectMultiple'),
      R.assoc('selectMultipleValue', searchCriteriaOptions),
      R.omit(['stringValue']),
    )(data);

    return newObject;
  };

  const getCreatableSearchCriteriaOptions = (data, options) => {
    const searchCriteriaOptions = R.map(
      (value) => {
        const match = R.find((option) => R.equals(option.value, value), options);

        return match || { value, label: value };
      },
      checkIsStringValueNotNil(data.stringValue),
    );

    const newObject = R.compose(
      R.set(R.lensProp('dataType'), 'selectMultipleCreatable'),
      R.assoc('selectMultipleValue', searchCriteriaOptions),
      R.omit(['stringValue']),
    )(data);

    return newObject;
  };

  const newReportList = R.map(
    (report) => {
      if (G.isNilOrEmpty(report.searchCriteria)) return report;

      const searchCriteria = R.map(
        (item) => {
          const { propertyName } = item;

          if (R.or(R.equals(propertyName, 'state'), R.includes('.state', propertyName))) {
            const stateOptions = R.reduce(
              (acc, grouped) => R.concat(acc, R.propOr([], 'options', grouped)),
              [],
              getGroupedByCountryStateOptionsByConfig(),
            );

            return getCreatableSearchCriteriaOptions(item, stateOptions);
          }

          if (R.includes(item.propertyName, R.keys(fieldNameToTransform))) return searchCriteriaObject(item);

          return item;
        },
        report.searchCriteria,
      );

      return R.set(R.lensProp('searchCriteria'), searchCriteria, report);
    },
    reports,
  );

  return newReportList;
};


const createdSearchCriteria = (data) => R.compose(
  R.set(R.lensProp('dataType'), 'string'),
  R.assoc('stringValue', R.join(',', R.map(R.prop('value'), R.path(['selectMultipleValue'], data)))),
  R.omit(['selectMultipleValue']),
)(data);

export const transformSearchCriteriaBeforeFilterPost = (data, reportType) => {
  if (G.isNilOrEmpty(data)) return data;

  const { dataType, propertyName, referenceName, referenceFieldName } = data;

  if (R.and(R.equals(propertyName, GC.FIELD_REFERENCES), G.isNotNilAndNotEmpty(referenceFieldName))) {
    const referencePrefix = `Ref (${G.getWindowLocale(...GC.referenceFieldNameLocaleMap[referenceFieldName])}): `;
    const value = R.slice(referencePrefix.length, Infinity, referenceName);

    return R.assoc(GC.FIELD_REFERENCE_NAME, value, data);
  }

  if (R.includes(dataType, ['selectMultiple', 'selectMultipleCreatable'])) {
    return createdSearchCriteria(data, 'selectMultipleCreatable');
  }

  if (R.equals(dataType, 'time')) return normalizeTimeSearchCriteria(data);

  if (R.equals(dataType, 'dateOnly')) return normalizeDateOnlySearchCriteria(data);

  return transformSearchItemByUomSystem(data, reportType);
};

export const transformFiltersValueForCharts = (filters, options) => {
  const checkIsStringValueNotNil = (stringValue) => {
    if (G.isNotNil(stringValue)) return R.split(',', stringValue);
    return '';
  };

  const searchCriteriaObject = (item) => {
    const filterValue = (option) => R.includes(option.value, checkIsStringValueNotNil(item.stringValue));
    const searchCriteriaOptions = R.map((item) => item, R.filter(filterValue, options));
    const newObject = R.compose(
      R.set(R.lensProp('dataType'), 'selectMultiple'),
      R.assoc('selectMultipleValue', searchCriteriaOptions),
      R.omit(['stringValue']),
      )(item);
    return newObject;
  };

  if (G.isNilOrEmpty(filters)) return [];
  const newFilter = filters.map((item) => {
    if (R.equals(item.propertyName, 'status')) return searchCriteriaObject(item);
    return item;
  });
  const newFilterObject = R.mergeRight(filters, newFilter);
  return newFilterObject;
};

export const renderReportBorderColor = (props) => {
  if (R.and(props.error, props.touched)) {
    return G.getTheme('forms.inputs.borderColorErr');
  }
  return G.getTheme('colors.dark.grey');
};

const getIndexesFromDnDResult = (result) => {
  const sourceIndex = R.path(['source', 'index'], result);
  const destinationIndex = R.path(['destination', 'index'], result);
  const destinationIndexChecked = G.ifElse(
    R.equals(destinationIndex, -1),
    0,
    destinationIndex,
  );
  return {
    sourceIndex,
    destinationIndexChecked,
  };
};

export const reorderFieldsOnDrugEnd = (sortedFields, result) => {
  if (R.isNil(R.prop('destination', result))) return sortedFields;

  const { sourceIndex, destinationIndexChecked } = getIndexesFromDnDResult(result);
  const fields = R.clone(sortedFields);
  const [removedEvent] = fields.splice(sourceIndex, 1);
  fields.splice(destinationIndexChecked, 0, removedEvent);
  return G.mapIndexed((stop, index) => R.assoc('sequence', R.inc(index), stop), fields);
};

const validateOperation = (report) => {
  let operationAbsent = false;
  report.searchCriteria.forEach((criteria) => {
    const condition = G.ifElse(
      R.equals(criteria.dataType, 'date'),
      R.and(G.isNilOrEmpty(criteria.timeUnit), G.isNilOrEmpty(criteria.operation)),
      G.isNilOrEmpty(criteria.operation),
    );
    if (condition) operationAbsent = true;
  });
  return operationAbsent;
};

const validateRange = (report) => {
  let rangeAbsent = false;
  report.searchCriteria.forEach((criteria) => {
    const { operation, dataType } = criteria;

    let condition = false;

    if (R.equals(operation, 'range')) {
      const rangeValues = R.equals(dataType, 'time')
        ? R.pick(['from', 'to'], criteria)
        : R.prop(`${dataType}Range`, criteria);

      condition = G.ifElse(
        R.all(G.isNilOrEmpty, R.values(rangeValues)),
        false,
        R.any(G.isNilOrEmpty, R.values(rangeValues)),
      );
    }

    if (condition) rangeAbsent = true;
  });

  return rangeAbsent;
};

// NOTE: check all report validate from report-format logic
export const isReportValid = (report) => {
  const rangeAbsent = validateRange(report);
  const operationAbsent = validateOperation(report);
  if (rangeAbsent) {
    const message = G.getWindowLocale(
      'message:range-fields-empty-or-filled-in',
      'Range fields should both be empty or both filled in',
    );
    G.showToastrMessageSimple('info', message);
    return false;
  }
  if (operationAbsent) {
    G.showToastrMessageSimple(
      'info',
      G.getWindowLocale(
        'messages:select-operation-for-report-filters',
        'Please, select operations for all selected filter fields',
      ),
    );
    return false;
  }
  return true;
};

export const isNotReportValid = (report) => R.not(isReportValid(report));

export const getAssessorials = ({ usedReport, assessorials }) => {
  const allowedTypes = [
    GC.TEL_REPORT,
    GC.CLO_REPORT,
    GC.PAYROLL_REPORT,
    GC.PIVOT_TEL_REPORT,
    GC.PIVOT_CLO_REPORT,
    GC.VENDOR_PAYROLL_REPORT,
    GC.CUSTOMER_INVOICE_REPORT,
    GC.PIVOT_DRIVER_PAYROLL_REPORT,
    GC.PIVOT_VENDOR_PAYROLL_REPORT,
    GC.PIVOT_CUSTOMER_INVOICE_REPORT,
  ];

  const type = R.path([GC.FIELD_TYPE], usedReport);

  if (R.includes(type, allowedTypes)) return assessorials;

  return [];
};

export const getAvailableForReports = (props, reportFieldList) => {
  const availableForReport = R.compose(
    R.reject(({ disableField }) => {
      if (G.isBoolean(disableField)) return disableField;
    }),
    R.pathOr([], ['availableFields']),
  )(props);

  return availableForReport.filter((af) => (
    R.not(reportFieldList.find((f) => R.equals(f.name, af.value)))
  ));
};

export const getAvailableForReportSort = (props, reportFieldList) => {
  const availableForSort = R.compose(
    R.reject(({ value, disableSort, disableFilter }) => {
      if (G.isBoolean(disableSort)) return disableSort;

      if (G.isDynamicField(value)) return true;

      return disableFilter;
    }),
    R.pathOr([], ['availableFields']),
  )(props);

  return availableForSort.filter((af) => (
    R.not(reportFieldList.find((f) => R.equals(f.name, af.value)))
  ));
};

export const getAvailableFilterFields = (props) => {
  const {
    usedReport,
    geoFencingZones,
    availableFields,
    additionalFilters,
    useAdditionalFilters,
    additionalFilterFields,
  } = props;

  if (G.isTrue(useAdditionalFilters)) return additionalFilters;

  const reportType = G.getPropFromObject(GC.FIELD_TYPE, usedReport);

  const filteredList = R.concat(R.filter(
    ({ disableFilter }) => R.not(disableFilter),
    R.or(availableFields, []),
  ), R.or(additionalFilterFields, []));

  const typesWithGeoFencingZones = [
    GC.TEL_REPORT,
    GC.CLO_REPORT,
    GC.PIVOT_CLO_REPORT,
    GC.PIVOT_TEL_REPORT,
    GC.CLO_EVENT_REPORT,
    GC.ROUTE_TEL_REPORT,
    GC.ROUTE_CLO_REPORT,
  ];

  if (G.notContain(reportType, typesWithGeoFencingZones)) {
    return filteredList;
  }

  const options = G.mapNameGuidToLabelValue(geoFencingZones);

  const dispatchReportGeoFencingZoneFields = [
    {
      options,
      type: 'string:select',
      value: GC.FIELD_ORIGIN_ZONE,
      name: G.getWindowLocale('titles:origin-zone', 'Origin Zone'),
      placeholder: {
        text: 'Choose',
        key: 'titles:choose',
      },
    },
    {
      options,
      type: 'string:select',
      value: GC.FIELD_DESTINATION_ZONE,
      name: G.getWindowLocale('titles:destination-zone', 'Destination Zone'),
      placeholder: {
        text: 'Choose',
        key: 'titles:choose',
      },
    },
  ];

  const geoFencingFieldNamesMap = {
    [GC.TEL_REPORT]: dispatchReportGeoFencingZoneFields,
    [GC.CLO_REPORT]: dispatchReportGeoFencingZoneFields,
    [GC.PIVOT_CLO_REPORT]: dispatchReportGeoFencingZoneFields,
    [GC.PIVOT_TEL_REPORT]: dispatchReportGeoFencingZoneFields,
    [GC.ROUTE_TEL_REPORT]: dispatchReportGeoFencingZoneFields,
    [GC.ROUTE_CLO_REPORT]: dispatchReportGeoFencingZoneFields,
    [GC.CLO_EVENT_REPORT]: [{
      options,
      type: 'string:select',
      value: GC.FIELD_LOCATION_ZONE,
      name: G.getWindowLocale('titles:location-zone', 'Location Zone'),
      placeholder: {
        text: 'Choose',
        key: 'titles:choose',
      },
    }],
  };

  return R.concat(filteredList, G.getPropFromObject(reportType, geoFencingFieldNamesMap));
};

export const setOptions = (options) => {
  if (G.isNotNilAndNotEmpty(options)) {
    return R.map((option) => ({
      [GC.FIELD_LABEL]: option.name,
      ...R.dissoc('options', option),
    }), options);
  }
};
